/**
 * Every admin endpoint in backend/api/openapi.yaml, and nothing else.
 *
 * One function per operationId, named after it. If you cannot find a function here for
 * something a screen needs, the contract does not have it — do not invent a field or a
 * route, raise the gap.
 */
import { api } from '@most/api-client';
import type {
  AdminCategory,
  AdminChatConversation,
  AdminDashboard,
  AdminNotification,
  AdminNotificationDetail,
  AdminOrderDetail,
  AdminOrderListItem,
  AdminPricingQueueItem,
  AdminProducer,
  AdminProduct,
  AdminPermission,
  AdminRole,
  AdminRoleWrite,
  AdminUserDetail,
  AdminUserListItem,
  AppConfig,
  AuditEntry,
  ChatAttachmentKind,
  ChatCall,
  ChatCallKind,
  ChatMessage,
  ChatMessagePage,
  Monitoring,
  CategoryInput,
  CreateUserRequest,
  NotificationChannel,
  NotificationStatus,
  OrderStatus,
  OrderStatusTarget,
  Paged,
  Role,
  UserStatus,
} from './types';

export interface PageParams {
  page?: number;
  per_page?: number;
}

// ── Config ───────────────────────────────────────────────────────────────────
/** GET /config — TRUCK_PALLETS / TRUCK_MAX_KG and the upload limits, mirrored from the server. */
export const getConfig = () => api.get<AppConfig>('/config');

// ── Profile (self-service) ───────────────────────────────────────────────────
//
// Not /admin/* — these are the shared self-service endpoints every session gets, and this
// SPA's profile screen (identity itself comes from @most/auth's useMe). Bodies are passed
// DIRECTLY: `api.post(path, body)` — the second parameter IS the JSON payload.

/** POST /me/password — 204. Revokes every OTHER session; this one survives. */
export const changeMyPassword = (body: { current_password: string; new_password: string }) =>
  api.post<void>('/me/password', body);

export const getTelegramStatus = () =>
  api.get<{ linked: boolean; linked_at?: string | null }>('/telegram/status');

/** Single-use, 10 min TTL. The bot captures chat_id when the user taps the link. */
export const createTelegramLinkToken = () =>
  api.post<{ deep_link_url: string; expires_at: string }>('/telegram/link-token');

/** Delivery falls back silently to email + in-app. Nothing blocks on Telegram. */
export const unlinkTelegram = () => api.delete<void>('/telegram/link');

// ── ADM-1 Dashboard ──────────────────────────────────────────────────────────
/** GET /admin/dashboard — the six counters + recent audit. One call, not six `per_page=1`s. */
export const getAdminDashboard = () => api.get<AdminDashboard>('/admin/dashboard');

// ── ADM-2 Approvals ──────────────────────────────────────────────────────────
export const listAdminProducers = (p: PageParams & { status?: UserStatus } = {}) =>
  api.get<Paged<AdminProducer>>('/admin/producers', { query: { ...p } });

export const getAdminProducer = (id: string) =>
  api.get<AdminProducer>(`/admin/producers/${id}`);

export const approveProducer = (id: string) =>
  api.post<{ status: UserStatus; approved_at: string }>(`/admin/producers/${id}/approve`);

/**
 * There is no `rejected` status. The account stays `pending_approval` with `rejection_reason`
 * set; the producer sees the reason verbatim, fixes the profile, re-submits — and that clears
 * the reason and re-queues them. The reason is therefore REQUIRED and is prose, not a code.
 */
export const rejectProducer = (id: string, reason: string) =>
  api.post<{ status: UserStatus }>(`/admin/producers/${id}/reject`, { reason });

// ── ADM-3 Pricing queue — THE CATALOG GATE ───────────────────────────────────
export const getPricingQueue = (p: PageParams = {}) =>
  api.get<Paged<AdminPricingQueueItem>>('/admin/pricing-queue', { query: { ...p } });

export const getAdminProduct = (id: string) => api.get<AdminProduct>(`/admin/products/${id}`);

/**
 * PUT /admin/products/{id}/selling-price — the ONLY thing that is stored is the absolute
 * `selling_price_per_piece`. The markup percentage in the calculator is a UI affordance and
 * is never persisted: a stored ratio would silently re-price the product every time the
 * producer moved their base price.
 *
 * Passing `null` CLEARS the price → the product vanishes from the buyer catalog immediately
 * and returns to the queue as unpriced. That is also the undo for a publish, and the same
 * mechanism the margin guard uses. There is no separate unpublish action.
 */
export const setSellingPrice = (id: string, selling_price_per_piece: string | null) =>
  api.put<AdminProduct>(`/admin/products/${id}/selling-price`, {
    selling_price_per_piece,
  });

// ── ADM-4 / ADM-5 Orders ─────────────────────────────────────────────────────
export const listAdminOrders = (
  p: PageParams & { status?: OrderStatus; buyer_id?: string; producer_id?: string } = {},
) => api.get<Paged<AdminOrderListItem>>('/admin/orders', { query: { ...p } });

/** The one DTO with no redactions: both price levels, margin, full buyer contacts. */
export const getAdminOrder = (number: string) =>
  api.get<AdminOrderDetail>(`/admin/orders/${encodeURIComponent(number)}`);

/** new → in_progress → completed | cancelled. Admin only. Terminal states do not reopen. */
export const setOrderStatus = (number: string, status: OrderStatusTarget) =>
  api.post<{ status: OrderStatus }>(
    `/admin/orders/${encodeURIComponent(number)}/status`,
    { status },
  );

// ── ADM-6 Users ──────────────────────────────────────────────────────────────
export const listAdminUsers = (
  p: PageParams & { role?: Role; status?: UserStatus; q?: string } = {},
) => api.get<Paged<AdminUserListItem>>('/admin/users', { query: { ...p } });

export const getAdminUser = (id: string) => api.get<AdminUserDetail>(`/admin/users/${id}`);

/**
 * The SERVER generates the password — the admin never chooses it — and returns it ONCE.
 * The SPA must show it exactly once, in a deliberately-dismissed dialog, and never store it.
 */
export const createUser = (body: CreateUserRequest) =>
  api.post<{ user: AdminUserListItem; temporary_password: string }>('/admin/users', body);

/** Blocking revokes every one of that user's sessions immediately. */
export const blockUser = (id: string) =>
  api.post<{ status: UserStatus }>(`/admin/users/${id}/block`);

export const unblockUser = (id: string) =>
  api.post<{ status: UserStatus }>(`/admin/users/${id}/unblock`);

/**
 * The last-resort remedy for the region's email-deliverability problem (Google is blocked in
 * Turkmenistan). Only meaningful while the user is `pending_email`. Audited as
 * `user.email_verified_manually`.
 */
export const verifyUserEmailManually = (id: string) =>
  api.post<{ status: UserStatus }>(`/admin/users/${id}/verify-email`);

/** Producer ratings are PUBLIC to buyers. 1.0–5.0, one decimal; null clears → "без рейтинга". */
export const rateProducer = (id: string, rating: string | null) =>
  api.put<{ rating: string | null }>(`/admin/producers/${id}/rating`, { rating });

/** Buyer ratings are ADMIN-ONLY — never shown to the buyer themself, never to producers. */
export const rateBuyer = (id: string, rating: string | null) =>
  api.put<{ rating: string | null }>(`/admin/buyers/${id}/rating`, { rating });

// ── ADM-7 Categories ─────────────────────────────────────────────────────────
export const listAdminCategories = () =>
  api.get<{ items: AdminCategory[] }>('/admin/categories');

export const getAdminCategory = (id: string) =>
  api.get<AdminCategory>(`/admin/categories/${id}`);

export const createCategory = (body: CategoryInput) =>
  api.post<AdminCategory>('/admin/categories', body);

/** No delete exists in the contract. Deactivating is the only removal — and it is reversible. */
export const updateCategory = (id: string, body: CategoryInput) =>
  api.put<AdminCategory>(`/admin/categories/${id}`, body);

// ── ADM-8 Notification health ────────────────────────────────────────────────
export const listAdminNotifications = (
  p: PageParams & {
    status?: NotificationStatus;
    channel?: NotificationChannel;
    order_id?: string;
  } = {},
) => api.get<Paged<AdminNotification>>('/admin/notifications', { query: { ...p } });

/**
 * Safe to show: the payload was audience-filtered when the outbox row was WRITTEN, not when
 * it is read. What Merdan sees here is exactly what the recipient got — which is the point.
 */
export const getAdminNotification = (id: string) =>
  api.get<AdminNotificationDetail>(`/admin/notifications/${id}`);

/** Resets the row to `pending` with attempts back to 0. Audited. */
export const retryNotification = (id: string) =>
  api.post<{ status: NotificationStatus }>(`/admin/notifications/${id}/retry`);

// ── ADM-9 Audit ──────────────────────────────────────────────────────────────
export const listAuditLog = (
  p: PageParams & {
    actor_id?: string;
    entity?: string;
    action?: string;
    from?: string;
    to?: string;
  } = {},
) => api.get<Paged<AuditEntry>>('/admin/audit-log', { query: { ...p } });

// ── Settings: Monitoring ─────────────────────────────────────────────────────
/** GET /admin/monitoring — DB/Redis liveness, outbox backlog, queue counters. Never 500s on a
 *  dependency being down; reporting that is its job. */
export const getMonitoring = () => api.get<Monitoring>('/admin/monitoring');

// ── Settings: Roles & Permissions ────────────────────────────────────────────
/** GET /admin/settings/permissions — the fixed capability catalog (the toggles grid). */
export const listPermissions = () =>
  api.get<{ items: AdminPermission[] }>('/admin/settings/permissions');

/** GET /admin/settings/roles — custom admin roles with grants and member counts. */
export const listRoles = () => api.get<{ items: AdminRole[] }>('/admin/settings/roles');

export const createRole = (body: AdminRoleWrite) =>
  api.post<AdminRole>('/admin/settings/roles', body);

export const updateRole = (id: string, body: AdminRoleWrite) =>
  api.put<AdminRole>(`/admin/settings/roles/${id}`, body);

export const deleteRole = (id: string) =>
  api.delete<void>(`/admin/settings/roles/${id}`);

/** Attach (role_id) or detach (null) a custom role to an admin account. */
export const assignRole = (userId: string, roleId: string | null) =>
  api.post<void>(`/admin/settings/roles/assign/${userId}`, { role_id: roleId });

// ── Support chat inbox ───────────────────────────────────────────────────────
//
// The admin side of the buyer|producer ↔ admin support channel. This is the one chat
// surface that names users — the admin sees everything by design.
export const adminListChats = () =>
  api.get<{ items: AdminChatConversation[]; unread_count: number }>('/admin/chats');

export const adminListChatMessages = (id: string, cursor?: string, signal?: AbortSignal) =>
  api.get<ChatMessagePage>(`/admin/chats/${id}/messages`, { query: { cursor }, signal });

export const adminSendChatMessage = (id: string, body: string) =>
  api.post<ChatMessage>(`/admin/chats/${id}/messages`, { body });

export const adminUploadChatAttachment = (
  id: string,
  kind: ChatAttachmentKind,
  file: File | Blob,
  caption?: string,
) => {
  const fd = new FormData();
  fd.append('kind', kind);
  if (caption) fd.append('body', caption);
  fd.append('file', file, file instanceof File ? file.name : 'recording.webm');
  return api.upload<ChatMessage>(`/admin/chats/${id}/attachments`, fd);
};

export const adminMarkChatRead = (id: string) =>
  api.post<{ updated_count: number }>(`/admin/chats/${id}/read`);

// Calls. Shared endpoints — the server derives the side from the session role; the admin
// must say WHICH thread to call.
export const startChatCall = (kind: ChatCallKind, conversationId?: string) =>
  api.post<ChatCall>('/chat/calls', { kind, conversation_id: conversationId });
export const getChatCall = (id: string) => api.get<ChatCall>(`/chat/calls/${id}`);
export const acceptChatCall = (id: string) => api.post<ChatCall>(`/chat/calls/${id}/accept`);
export const declineChatCall = (id: string) => api.post<ChatCall>(`/chat/calls/${id}/decline`);
export const endChatCall = (id: string) => api.post<ChatCall>(`/chat/calls/${id}/end`);
export const postChatCallSignal = (id: string, data: string) =>
  api.post(`/chat/calls/${id}/signals`, { data });
export const getChatCallSignals = (id: string) =>
  api.get<{ signals: string[] }>(`/chat/calls/${id}/signals`);
